import type { ComputedRef, InjectionKey } from 'vue'
import type {
  StatisticCardAppearanceProps,
  StatisticCardControlType,
  StatisticCardItemData,
  StatisticCardSelectionValue,
} from './types'

/**
 * Group 上下文：由 StatisticCardGroup 向内部所有卡片透传，
 * 用于告知当前卡片"我在分组容器内"，从而改变边框、透明占位等表现。
 */
export interface StatisticCardGroupContext {
  inGroup: boolean
}

export const statisticGroupKey: InjectionKey<StatisticCardGroupContext> = Symbol('statisticGroupKey')

/**
 * 容器级卡片数据上下文：由 List / Grid / Formula 注入，
 * 用于 tooltip 渲染函数的第三参 `data`。
 */
export interface StatisticCardDataContext {
  data: ComputedRef<StatisticCardItemData[]> | { value: StatisticCardItemData[] }
}

export const statisticCardDataKey: InjectionKey<StatisticCardDataContext> =
  Symbol('statisticCardDataKey')

/**
 * 外观上下文：由容器（List / Grid / Formula / Group / SuperGroup）向内部卡片
 * 传递统一的外观 props（size / border / equalHeight / numberScroll / subIndicatorsLayout / reverseColor），
 * 替代 React 端通过 cloneElement 逐个覆盖 props 的写法。
 *
 * 卡片自身 props 优先级高于上下文——卡片显式传入的值不会被覆盖。
 */
export interface StatisticCardAppearanceContext {
  appearance: ComputedRef<StatisticCardAppearanceProps>
}

export const statisticCardAppearanceKey: InjectionKey<StatisticCardAppearanceContext> =
  Symbol('statisticCardAppearanceKey')

/**
 * 选择上下文：由启用了 selection 的容器注入，
 * 卡片消费该上下文以决定：
 * - 是否渲染 selection 控件（radio / checkbox）
 * - 卡片是否处于 checked 态
 * - 点击卡片时应触发何种切换
 * - 是否受到 maxLength / minLength 约束而进入 disabled 态
 */
export interface StatisticCardSelectionContext {
  controlType: ComputedRef<StatisticCardControlType>
  isChecked: (id: StatisticCardSelectionValue) => boolean
  /** 判断当前卡片是否因达到上/下限而不可再切换 */
  isLocked: (id: StatisticCardSelectionValue) => boolean
  /** 卡片点击时调用，容器负责根据 controlType 内部完成单选/多选切换 */
  toggle: (id: StatisticCardSelectionValue) => void
}

export const statisticCardSelectionKey: InjectionKey<StatisticCardSelectionContext> =
  Symbol('statisticCardSelectionKey')